<template>
  <div class="step-container client">
    <v-form name="Finish" class="form" data-test-id="finish-form">
      <v-container class="steps-step-container">
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="finish-header">
              <span class="headerlight" :class="{ 'pulsing': hasSyncingSteps }" v-if="hasSyncingSteps" data-test-id="syncing-message">
                Please <strong>wait</strong> while your answers are being saved.<br> This may take a few moments.
              </span>
              <span class="headerlight" v-else-if="!isAllComplete" data-test-id="incomplete-message">
                You're
                <strong>close to finishing!</strong>
                Here's what you've
                <strong>skipped:</strong>
              </span>
              <span v-else class="headerlight" data-test-id="complete-message">
                Everything is <strong> Complete! </strong> Press
                <strong> Finish </strong> to finalize.
              </span>
            </h1>
          </v-col>
        </v-row>
        <div class="justify-center steps-table" v-if="!finishing" data-test-id="incomplete-steps-container">
          <incomplete-steps-list 
            :steps="mappedIncompleteSteps" 
            @step-click="next"
            data-test-id="incomplete-steps-list"
          />
        </div>
        <required-documents-list
          v-if="!finishing"
          :documents="requiredDocuments"
          @upload-document="uploadDoc"
          data-test-id="required-documents-list"
        />
      </v-container>
    </v-form>
    <bottom-nav
      :nextText="'Finish'"
      :nextDisabled="hasSyncingSteps || !isAllComplete"
      :class="{ 'pulse-button': hasSyncingSteps }"
      v-on:go-prev="prev()"
      v-on:go-next="saveFinish()"
      data-test-id="finish-nav"
    ></bottom-nav>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BottomNav from '@/components/common/BottomNav';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import UnathenticatedClientMixin from '@/common/mixins/unauthenticated.client.mixin';
import StepsMixin from '@/common/mixins/steps.mixin';
import DocumentsMixin from '@/common/mixins/documents.mixin';
import ProgressPollingMixin from '@/common/mixins/progress-polling.mixin';
import IncompleteStepsList from '@/components/client/IncompleteStepsList';
import RequiredDocumentsList from '@/components/client/RequiredDocumentsList';
import Storage from '@/services/storage.service';
import {
  MENUS,
  CLIENT_EXIT_KEYS,
  LOCAL_KEYS,
  AUTH_TYPES,
} from '@/common/constants';
import {
  GET_INTERVIEW_PROGRESS,
  FINISH_INTERVIEW,
} from '@/stores/actions.type';
import {
  NOTIFICATION_TYPES,
  NotificationService,
} from '@/services/notification.service';

export default {
  name: 'Finish',

  components: { 
    BottomNav, 
    IncompleteStepsList,
    RequiredDocumentsList
  },
  
  mixins: [
    InterviewClientMixin, 
    UnathenticatedClientMixin, 
    StepsMixin,
    DocumentsMixin,
    ProgressPollingMixin
  ],

  data: () => ({
    finishing: false
  }),

  validations() {
    return {};
  },

  computed: {
    ...mapGetters(['clientProgress']),

    mappedIncompleteSteps() {
      let customQuestionCount = 0;

      return this.incompleteSteps.map(step => {
        let name = step.name && step.name.trim() ? step.name : null;

        const isCustomQuestion =
            step.route.name === MENUS.CLIENT.CUSTOM_QUESTIONS.id &&
            step.route.params &&
            step.route.params.questionId


        // Special handling for custom questions
        if (isCustomQuestion) {
          if (!name) {
            customQuestionCount++;
            name = `Custom Question ${customQuestionCount}`;
          }

          return {
            ...step,
            name,
            status: this.getStepStatus(step),
            filled: this.isPageComplete(step.route.name, null, step.route.params.questionId)
          };
        }

        // Handle null/undefined names with fallback
        if (!name) {
          // Use route name as fallback, converting from snake_case to Title Case
          name = step.route.name
            ? step.route.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            : 'Incomplete Step';
        }

        // Standard handling for other page types
        return {
          ...step,
          name,
          status: this.getStepStatus(step),
          filled: this.isPageComplete(step.route.name, step.route.params)
        };
      });
    },

    areStepsComplete() {
      return this.mappedIncompleteSteps.length === 0;
    },

    isAllComplete() {
      return this.areStepsComplete && this.documentsComplete;
    },
    
    documentsComplete() {
      return this.areDocumentsComplete(this.requiredDocuments);
    },

    hasSyncingSteps() {
      return this.mappedIncompleteSteps.some(step => step.status === 'IN_PROGRESS');
    },
    
    requiredDocuments() {
      return this.getFormattedDocuments();
    }
  },

  methods: {
    /* Upload a document */
    uploadDoc(doc) {
      this.finishing = true;
      
      this.uploadDocument(doc)
        .finally(() => {
          this.$store
            .dispatch(GET_INTERVIEW_PROGRESS)
            .finally(() => (this.finishing = false));
        });
    },

    /* Update the saveFinish method for better organization */
    saveFinish() {
      // Determine the exit route based on authentication status
      const isClient = Storage.getLocal(LOCAL_KEYS.AUTH_TYPE) === AUTH_TYPES.UNAUTHENTICATED_CLIENT;
      let route = this.getExitRoute(isClient);

      // Only call finish when all steps are complete and synced
      if (this.isAllComplete) {
        this.finishAndExit(route);
      } else {
        this.exitWithoutFinishing(route);
      }
    },
    
    /* Get the appropriate exit route */
    getExitRoute(isClient) {
      if (isClient) {
        return {
          name: MENUS.INTERVIEW.UNAUTHENTICATED.id,
          params: {
            flag: this.isAllComplete
              ? CLIENT_EXIT_KEYS.EXIT_COMPLETE
              : CLIENT_EXIT_KEYS.EXIT_INCOMPLETE_SAVE,
          }
        };
      }
      return { name: MENUS.CLIENTS.id };
    },
    
    /* Finish the interview and navigate to exit route */
    finishAndExit(route) {
      this.stopPolling();
      this.finishing = true;

      // Make one final progress check
      this.$store.dispatch(GET_INTERVIEW_PROGRESS)
        .then(() => {
          // Wait a moment to ensure UI updates
          setTimeout(() => {
            if (this.hasSyncingSteps) {
              this.handleSyncInProgress();
              return;
            }

            // If check passes, proceed with finishing
            this.$store.dispatch(FINISH_INTERVIEW)
              .then(() => {
                this.goNext(route);
              })
              .catch(this.handleFinishError);
          }, 300);
        })
        .catch(error => {
          this.handleProgressCheckError(error);
        });
    },
    
    /* Handle case where sync is still in progress */
    handleSyncInProgress() {
      this.finishing = false;
      NotificationService.alert({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Sync Error',
        message: 'Please wait until all questionnaire responses have been synced to the CRM before finishing.',
        okTitle: 'Ok'
      });
      this.startPolling();
    },
    
    /* Handle error in finishing the interview */
    handleFinishError(response) {
      this.finishing = false;
      this.startPolling();
      NotificationService.alert({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Error',
        message: 'Error Finishing the interview process.',
        okTitle: 'Ok',
      }, response);
    },
    
    /* Handle error checking progress */
    handleProgressCheckError(error) {
      this.finishing = false;
      this.startPolling(); // Resume polling
      console.error('Error checking progress:', error);
      NotificationService.alert({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Sync Status Error',
        message: 'Could not verify if all responses are synced. Please try again.',
        okTitle: 'Ok'
      });
    },
    
    /* Exit without finishing the interview */
    exitWithoutFinishing(route) {
      this.finishing = true;
      this.goNext(route);
    },

    /* Nav to route selected */
    next(route) {
      this.goNext(route);
    },

    /* Nav to Previous Page in flow */
    prev() {
      // Check for custom questions first
      // let prev = this.getPreviousCustomQuestionRoute();
      
      this.goPrev(this.pages[this.pageIndex - 1]);
    }
  },

  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.FINISH.id);
    this.startPolling();
  },

  watch: {
    finishing(newVal) {
      if (newVal) {
        this.stopPolling();
      } else if (!this.isAllComplete) {
        this.startPolling();
      }
    },
    
    isAllComplete(newVal) {
      if (newVal) {
        this.stopPolling();
      } else if (!this.finishing) {
        this.startPolling();
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.step-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;

  .form {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 100px; // Extra padding to account for the bottom nav
  }
}

.steps-step-container {
  max-width: 1024px;
  width: 100%;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.steps-table {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
}

.headerlight {
  strong {
    color: var(--primary-color);
  }

  &.pulsing {
    animation: pulse-header 2s ease-in-out infinite;
  }
}

@keyframes pulse-header {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--v-primary-base), 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(var(--v-primary-base), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--v-primary-base), 0);
  }
}

.pulse-button {
  :deep(.v-btn) {
    animation: pulse 2s infinite;
    position: relative;
    
    &:disabled {
      animation: none;
    }
  }
}

// Update bottom nav positioning
:deep(.bottom-nav) {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  z-index: 10;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
}
</style>
